import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import toast from 'react-hot-toast';
import { Download, Clock, X } from 'lucide-react';
import { api } from '../services/api';

// dayjs plugins are global/idempotent — ensure they're on even if this renders
// before the Machines page has loaded (e.g. a direct visit to /settings).
dayjs.extend(utc);
dayjs.extend(timezone);

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const downloadBase64File = (base64: string, mime: string, filename: string) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
  const blob = new Blob([bytes], { type: mime });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

interface Props {
  machineType?: string;        // defaults to water_jacket
  triggerClassName?: string;   // override the trigger button's styling
  triggerLabel?: string;       // defaults to "Stoppage"
}

/**
 * Self-contained "Stoppage Report — All Machines" export: a trigger button that
 * opens a dialog (period + shift + Excel/PDF) and downloads the report. Used on
 * both the Machines page toolbar and the Settings page.
 */
const StoppageReportExport: React.FC<Props> = ({
  machineType = 'water_jacket',
  triggerClassName,
  triggerLabel = 'Stoppage',
}) => {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState<'today' | 'yesterday' | 'last3days' | 'custom'>('today');
  const [shift, setShift] = useState<'both' | 'day' | 'night'>('both');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [busy, setBusy] = useState(false);

  const handleExport = async (format: 'xlsx' | 'pdf') => {
    const params = new URLSearchParams();
    params.append('format', format);
    params.append('machineType', machineType);
    params.append('shift', shift);
    if (preset !== 'custom') {
      params.append('preset', preset);
    } else {
      if (!startDate || !endDate) {
        toast.error('Please select start and end dates');
        return;
      }
      const start = dayjs.tz(`${startDate}T00:00:00`, 'Asia/Kolkata');
      const end = dayjs.tz(`${endDate}T23:59:59`, 'Asia/Kolkata');
      if (!start.isValid() || !end.isValid()) {
        toast.error('Invalid date format');
        return;
      }
      if (end.isBefore(start)) {
        toast.error('End date must be after start date');
        return;
      }
      if (end.diff(start, 'month', true) > 2) {
        toast.error('Date range cannot exceed 2 months');
        return;
      }
      params.append('startDate', start.toISOString());
      params.append('endDate', end.toISOString());
    }
    setBusy(true);
    try {
      const resp = await api.get(`/machine-logs/stoppage-report-all?${params.toString()}`);
      if (format === 'pdf') {
        downloadBase64File(resp.data.pdf, 'application/pdf', resp.data.filename || 'all-machines-stoppage.pdf');
      } else {
        downloadBase64File(resp.data.excel, XLSX_MIME, resp.data.filename || 'all-machines-stoppage.xlsx');
      }
      toast.success('Stoppage report downloaded');
      setOpen(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to export stoppage report');
    } finally {
      setBusy(false);
    }
  };

  const defaultTrigger =
    'flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 text-xs font-black uppercase tracking-widest rounded-xl bg-red-600 text-white hover:bg-red-700 transition-all shadow-lg active:scale-95';

  return (
    <>
      <button onClick={() => setOpen(true)} className={triggerClassName || defaultTrigger}>
        <Clock className="w-4 h-4 text-white" />
        <span>{triggerLabel}</span>
      </button>

      {/* Portal to <body>: the trigger sits inside the Machines status bar, which uses
          backdrop-blur. backdrop-filter makes that bar the containing block for
          position:fixed children, so an in-place modal got clipped to the bar. */}
      {open && createPortal(
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ zIndex: 100000 }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Stoppage Report — All Machines</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Period</label>
                <div className="grid grid-cols-2 gap-2">
                  {([['today', 'Today'], ['yesterday', 'Yesterday'], ['last3days', 'Last 3 Days'], ['custom', 'Custom']] as const).map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => setPreset(val)}
                      className={`px-3 py-2 text-sm font-medium rounded-md border ${preset === val ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Shift</label>
                <div className="grid grid-cols-3 gap-2">
                  {([['both', 'Both'], ['day', 'Day'], ['night', 'Night']] as const).map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => setShift(val)}
                      className={`px-3 py-2 text-sm font-medium rounded-md border ${shift === val ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {preset === 'custom' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Start date</label>
                    <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">End date</label>
                    <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md" />
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500">Covers every water-jet machine's stops in the selected period.</p>
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => handleExport('xlsx')}
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                >
                  <Download className="h-4 w-4" /> {busy ? 'Preparing…' : 'Excel'}
                </button>
                <button
                  onClick={() => handleExport('pdf')}
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  <Download className="h-4 w-4" /> {busy ? 'Preparing…' : 'PDF'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default StoppageReportExport;
